/**
 * Слоты оборудования модульной схемы — чистые функции без обращений к API.
 *
 * Слот — место под оборудование на схеме («КШ-2» узла ВР-03): ограничения + количество
 * + позиция + статус. Записи слотов лежат в БД (m_to_m_module_scheme_slots), но создаются
 * лениво — при первом выборе позиции. До этого строка «виртуальная»: экземпляры оборудования
 * берутся с чертежей узлов (_detected_elements), а запись слота (db) пуста.
 */

/** Активный цвет: слот под курсором, выделенные для массового назначения слоты и их панель действий. */
export const ACTIVE_COLOR = "#7B1FA2";

/** Статусы слота: draft → purchased (ставит пользователь) → installed (ставит система после документа). */
export const SLOT_STATUS = {
  draft: { label: "Набросок", color: "grey lighten-2", textColor: "black", stroke: "#2e9e5b" },
  purchased: { label: "Куплено", color: "orange darken-2", textColor: "white", stroke: "#ef6c00" },
  installed: { label: "Установлено", color: "teal darken-1", textColor: "white", stroke: "#00897b" },
};

const OPERAND_SYMBOLS = { eq: "=", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" };

/** Условие подбора для показа: «DN ≥ 20». */
export function formatRule(rule) {
  const symbol = OPERAND_SYMBOLS[rule.operand] || rule.operand;
  return `${rule.name || `#${rule.id_characteristic}`} ${symbol} ${rule.value}`;
}

/** «1 слот», «2 слота», «5 слотов» */
export function pluralSlots(n) {
  const last = n % 10;
  if (n % 100 >= 11 && n % 100 <= 14) return `${n} слотов`;
  if (last === 1) return `${n} слот`;
  return last >= 2 && last <= 4 ? `${n} слота` : `${n} слотов`;
}

/** «1 позиция», «2 позиции», «5 позиций» */
export function pluralPositions(n) {
  const last = n % 10;
  if (n % 100 >= 11 && n % 100 <= 14) return `${n} позиций`;
  if (last === 1) return `${n} позиция`;
  return last >= 2 && last <= 4 ? `${n} позиции` : `${n} позиций`;
}

export function slotRef(uid, key) {
  return `${uid}|${key}`;
}

function dbAddress(uid, idElement, instance) {
  return `${uid}:${idElement}:${instance}`;
}

/** Выбранная позиция слота: у купленного — снимок на момент покупки, у наброска — название по каталогу. */
function chosenOf(db, names) {
  if (!db) return null;
  const snapshot = db.nomenclature_snapshot;
  if (db.status !== "draft" && snapshot) {
    return {
      id: snapshot.id,
      // как в списке подбора: «Семейство Название»; у снимков без семейства — одно название
      name: `${snapshot.family_name || ""} ${snapshot.name || ""}`.trim(),
      vendorCode: snapshot.vendor_code || null,
      price: snapshot.price === undefined ? null : snapshot.price,
    };
  }
  if (db.id_nomenclature) {
    return { id: db.id_nomenclature, name: names[db.id_nomenclature] || "", vendorCode: null, price: null };
  }
  return null;
}

/**
 * Вариант сборки модуля, который реально нарисуется: из layout.nodes[].variant, а если у узла такого нет — первый
 * (то же правило, что на бэке, DSchemeNodes::resolveVariant).
 */
export function resolveVariant(variants, wanted) {
  return wanted != null && (variants || [1]).includes(wanted) ? wanted : 1;
}

/**
 * Модули схемы по порядку, внутри — экземпляры оборудования их чертежей, сведённые с записями слотов.
 * Экземпляры другого варианта сборки узла (подпапки «2», «3» в Elements) не показываются: у них нет места
 * на картинке выбранного варианта; общие для всех вариантов (variant = null) показываются всегда.
 *
 * @param {Object} p
 * @param {Object} p.layout схема: layout.nodes[] — размещения узлов (uid, id_scheme_node)
 * @param {Object} p.elementsByNode id узла каталога → { code, elements: _detected_elements[], variants: _detected_variants[], functionalities: _functionalities[] }
 * @param {Object[]} p.slots записи слотов из API
 * @param {Object} p.names id номенклатуры → название (для набросков)
 * @returns {{ groups: Object[], orphans: Object[] }} orphans — записи слотов, которых больше нет на чертежах
 *          (узел убрали или заменили SVG): купленные не удаляем, показываем отдельно
 */
export function buildSlotGroups({ layout, elementsByNode, slots, names }) {
  const nodes = (layout && layout.nodes) || [];
  const byAddress = new Map();
  (slots || []).forEach((slot) => {
    byAddress.set(dbAddress(slot.node_uid, slot.id_scheme_node_element, slot.element_instance), slot);
  });
  const matched = new Set();

  const total = {};
  nodes.forEach((n) => (total[n.id_scheme_node] = (total[n.id_scheme_node] || 0) + 1));
  const seen = {};

  const groups = [];
  nodes.forEach((node) => {
    const info = elementsByNode[node.id_scheme_node];
    if (!info) return;
    // один узел может стоять в схеме несколько раз — различаем по порядку
    seen[node.id_scheme_node] = (seen[node.id_scheme_node] || 0) + 1;
    const variants = info.variants && info.variants.length ? info.variants : [1];
    const variant = resolveVariant(variants, node.variant);
    const withNumber = total[node.id_scheme_node] > 1 ? `${info.code} (${seen[node.id_scheme_node]})` : info.code;
    const title = variants.length > 1 ? `${withNumber} · вариант ${variant}` : withNumber;

    const rows = [];
    info.elements.forEach((element) => {
      (element.instances || []).forEach((instance) => {
        if (instance.variant != null && instance.variant !== variant) return;
        const key = `${element.id}:${instance.n}`;
        const address = dbAddress(node.uid, element.id, instance.n);
        const db = byAddress.get(address) || null;
        if (db) matched.add(address);
        const status = db ? db.status : "draft";
        rows.push({
          ref: slotRef(node.uid, key),
          uid: node.uid,
          idNode: node.id_scheme_node,
          groupTitle: title,
          key,
          idElement: element.id,
          n: instance.n,
          label: instance.label,
          elementName: element.name_ru || element.code,
          icon: element.icon || (element._file && element._file.url) || undefined,
          // рамка на чертеже узла — по ней схема рисует подсветку и ловит клик
          box: { x: instance.x, y: instance.y, w: instance.width, h: instance.height },
          db,
          status,
          frozen: status !== "draft",
          quantity: db ? db.quantity : 1,
          chosen: chosenOf(db, names),
          constraints: db ? db.constraints || [] : null,
        });
      });
    });
    if (rows.length) {
      groups.push({
        uid: node.uid,
        idNode: node.id_scheme_node,
        title,
        variant,
        variants,
        functionalities: info.functionalities || [],
        slots: rows,
      });
    }
  });

  const orphans = (slots || [])
    .filter((slot) => !matched.has(dbAddress(slot.node_uid, slot.id_scheme_node_element, slot.element_instance)))
    .map((db) => {
      const info = elementsByNode[db.id_scheme_node];
      return {
        ref: `orphan|${db.id}`,
        db,
        label: db.label || `#${db.id}`,
        groupTitle: info ? info.code : db.id_scheme_node ? `узел #${db.id_scheme_node}` : "узел удалён",
        status: db.status,
        frozen: db.status !== "draft",
        quantity: db.quantity,
        chosen: chosenOf(db, names),
        constraints: db.constraints || [],
      };
    });

  return { groups, orphans };
}

/** Сливает ответ API (записи слотов) в текущий список: по id заменяем, новые добавляем. */
export function mergeSlots(current, updated) {
  const byId = new Map(current.map((slot) => [slot.id, slot]));
  updated.forEach((slot) => byId.set(slot.id, slot));
  return [...byId.values()];
}

/** id номенклатуры, у которых нужно узнать название (наброски; у купленных есть снимок). */
export function draftNomenclatureIds(slots) {
  return (slots || []).filter((s) => s.status === "draft" && s.id_nomenclature).map((s) => s.id_nomenclature);
}
